import { Classes, HTMLTable, Popover } from "@blueprintjs/core";
import { Money } from "modules/common/components/money/Money";
import { GeneralIcon } from "modules/common/components/planr/icon/Generalcon";
import { PlanrTag } from "modules/common/components/planr/tag/PlanrTag";
import { prettyRound } from "modules/common/services/formatting/numbers";
import { OverheadBreakdownSnapshotType } from "modules/orders-manage/models/order";
import React from "react";
import styles from "./OverheadDetails.module.scss";

/**
 * Раскрытие формулы накладных расходов проекта: сколько дала фиксированная часть,
 * сколько переменная и из чего сложились ставки квартала.
 */
export const OverheadDetails: React.FC<OverheadDetailsProps> = ({ details }) => {
  if (!details) {
    return null;
  }

  return (
    <Popover
      position="left"
      usePortal={true}
      interactionKind="hover-target"
      content={
        <div style={{ backgroundColor: "white", padding: "1rem" }} className={`${Classes.ELEVATION_1} ${styles.popover}`}>
          <p>
            <PlanrTag type="success">
              Накладные расходы: <Money amount={details.total} /> ({prettyRound(details.percents)}% от выручки)
            </PlanrTag>
          </p>

          <HTMLTable compact={true}>
            <tbody>
              <tr>
                <td>Фиксированная часть</td>
                <td className="righted">{prettyRound(details.fixedShare)}% норматива</td>
                <td className="righted">
                  <Money amount={details.fixed} />
                </td>
              </tr>
              <tr>
                <td>Переменная часть</td>
                <td className="righted">{prettyRound(100 - details.fixedShare)}% норматива</td>
                <td className="righted">
                  <Money amount={details.variable} />
                </td>
              </tr>
              <tr>
                <td>Выручка проекта</td>
                <td />
                <td className="righted">
                  <Money amount={details.income} />
                </td>
              </tr>
              <tr>
                <td>Собственные силы</td>
                <td />
                <td className="righted">
                  <Money amount={details.ownSpendings} />
                </td>
              </tr>
            </tbody>
          </HTMLTable>

          {details.quarters.length > 0 && (
            <>
              <h4 className={styles.header}>По кварталам</h4>
              <HTMLTable compact={true} className={styles.quarters}>
                <thead>
                  <tr>
                    <th>Квартал</th>
                    <th className="righted">Норматив</th>
                    <th className="righted">Фикс. ставка</th>
                    <th className="righted">Перем. ставка</th>
                    <th className="righted">Фикс. часть</th>
                    <th className="righted">Перем. часть</th>
                  </tr>
                </thead>
                <tbody>
                  {details.quarters.map((quarter) => (
                    <tr key={quarter.quarter}>
                      <td>{quarter.quarter}</td>
                      <td className="righted">{prettyRound(quarter.overheadPercents)}%</td>
                      <td className="righted">{prettyRound(quarter.fixedPercents)}%</td>
                      <td className="righted">{prettyRound(quarter.variableRate)} ₽/₽</td>
                      <td className="righted">
                        <Money amount={quarter.fixed} />
                      </td>
                      <td className="righted">
                        <Money amount={quarter.variable} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </HTMLTable>
              <p className={styles.hint}>
                Фиксированная часть — минимальный порог от выручки проекта, переменная — доля накладных пропорционально
                себестоимости собственного труда.
              </p>
            </>
          )}
        </div>
      }
    >
      <GeneralIcon type="general-question" size={14} style={{ cursor: "help", color: "#EA561E" }} />
    </Popover>
  );
};

interface OverheadDetailsProps {
  details: OverheadBreakdownSnapshotType | null | undefined;
}
